import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Eye, EyeOff, CheckCircle2 } from "lucide-react";
import { BrandLockup } from "@/components/LogoMark";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const inputCls = "rounded-none focus:ring-2 focus:ring-blue-800";
const btnCls = "w-full rounded-none bg-blue-900 hover:bg-blue-800 text-white uppercase tracking-widest text-xs h-11";

export default function Invite({ base = "/invites" }) {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, login, register, loading } = useAuth();
  const [inv, setInv] = useState(null);
  const [fatal, setFatal] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ name: "", password: "", phone: "" });
  const [vakalatnama, setVakalatnama] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const isBrief = inv?.role === "advocate_change";

  useEffect(() => {
    api.get(`${base}/${token}`)
      .then(({ data }) => setInv(data))
      .catch((e) => setFatal(errMsg(e)));
  }, [token, base]);

  const accept = async () => {
    if (isBrief && !vakalatnama) { setError("Attach the signed vakalatnama (PDF) to accept the brief"); return; }
    setBusy(true);
    setError("");
    try {
      let body;
      if (isBrief) { body = new FormData(); body.append("vakalatnama", vakalatnama); }
      const { data } = await api.post(`${base}/${token}/accept`, body);
      toast.success("You are now on record in this matter");
      navigate(`/cases/${data.case_id}${data.half_pending ? "?pay=1" : ""}`);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (inv.account_exists) await login(inv.email, form.password);
      else await register({ ...form, email: inv.email, role: inv.user_role });
      await accept();
    } catch (err) {
      setError(errMsg(err));
      setBusy(false);
    }
  };

  if (fatal) {
    return (
      <Shell>
        <p className="text-sm text-red-800 dark:text-red-400 mt-6" data-testid="invite-invalid">{fatal}</p>
      </Shell>
    );
  }
  if (!inv || loading) return <Shell><p className="label-caps mt-6">Opening invitation…</p></Shell>;

  const dead = inv.used || inv.expired;

  return (
    <Shell>
      <div data-testid="invite-page">
        <p className="label-caps mt-8">You are invited as {inv.role_label}</p>
        <h1 className="font-display text-3xl mt-2" data-testid="invite-case-title">{inv.case_title}</h1>
        <p className="font-mono text-xs text-muted-foreground mt-2" data-testid="invite-case-ref">
          {inv.case_number ? `${inv.case_number} · ${inv.case_ref}` : inv.case_ref}
        </p>
        <p className="text-sm text-muted-foreground mt-3">
          {inv.claimant} <span className="mx-2 text-blue-900 dark:text-blue-400">versus</span> {inv.respondent}
        </p>

        {dead ? (
          <p className="mt-8 text-sm text-red-800 dark:text-red-400" data-testid="invite-dead">
            {inv.used ? "This invitation has already been used." : "This invitation has expired."}
            {" "}Ask for a fresh link.
          </p>
        ) : user ? (
          <div className="mt-8 space-y-5">
            <p className="text-sm">
              Signed in as <strong>{user.email}</strong>. Joining will place you on record on the
              {" "}{inv.side}'s side and open your share of the case fee for payment.
            </p>
            {isBrief && <VakalatnamaField file={vakalatnama} onChange={setVakalatnama} />}
            {error && <p className="text-sm text-red-800 dark:text-red-400" data-testid="invite-error">{error}</p>}
            <Button onClick={accept} disabled={busy} data-testid="invite-accept-button" className={btnCls}>
              {busy ? "Joining…" : "Join this matter"}
            </Button>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="invite-form">
            <div className="space-y-2">
              <Label className="label-caps">Email on the invitation</Label>
              <Input data-testid="invite-email" value={inv.email} readOnly className={`${inputCls} bg-muted`} />
            </div>
            {!inv.account_exists && (
              <>
                <div className="space-y-2">
                  <Label className="label-caps">Your full name</Label>
                  <Input data-testid="invite-name-input" required value={form.name}
                    onChange={(e) => set("name", e.target.value)} className={inputCls} />
                </div>
                <div className="space-y-2">
                  <Label className="label-caps">Mobile number</Label>
                  <Input data-testid="invite-phone-input" value={form.phone} placeholder="+91"
                    onChange={(e) => set("phone", e.target.value)} className={inputCls} />
                </div>
              </>
            )}
            <div className="space-y-2">
              <Label className="label-caps">{inv.account_exists ? "Your password" : "Choose a password"}</Label>
              <div className="relative">
                <Input data-testid="invite-password-input" type={show ? "text" : "password"} required minLength={6}
                  value={form.password} onChange={(e) => set("password", e.target.value)}
                  className={`${inputCls} pr-10`} />
                <button type="button" data-testid="invite-password-toggle" onClick={() => setShow((s) => !s)}
                  aria-label={show ? "Hide password" : "Show password"}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors duration-200">
                  {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            {isBrief && <VakalatnamaField file={vakalatnama} onChange={setVakalatnama} />}
            {error && <p className="text-sm text-red-800 dark:text-red-400" data-testid="invite-error">{error}</p>}
            <Button type="submit" disabled={busy} data-testid="invite-submit-button" className={btnCls}>
              {busy ? "Please wait…" : inv.account_exists ? "Sign in and join" : "Create profile and join"}
            </Button>
            <p className="flex items-start gap-2 text-[11px] text-muted-foreground">
              <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              This link works once and is tied to {inv.email}. Your share of the case fee
              {inv.role === "respondent_advocate" ? " for your side" : ""} is payable immediately after joining.
            </p>
          </form>
        )}
      </div>
    </Shell>
  );
}

const VakalatnamaField = ({ file, onChange }) => (
  <div className="space-y-2">
    <Label className="label-caps">Signed vakalatnama (PDF, required)</Label>
    <Input type="file" accept="application/pdf" required data-testid="invite-vakalatnama-input" className={inputCls}
      onChange={(e) => onChange(e.target.files?.[0] || null)} />
    <p className="text-[11px] text-muted-foreground">{file ? file.name : "Placed on the case record for the tribunal on acceptance."}</p>
  </div>
);

const Shell = ({ children }) => (
  <div className="min-h-screen flex items-center justify-center px-6 py-16">
    <div className="w-full max-w-md">
      <BrandLockup />
      {children}
    </div>
  </div>
);
